import { Component, OnInit, inject } from "@angular/core";
import { RiskWorkspaceModel } from "@app/risk/models/RiskWorkspaceModel";
import { RiskDataService } from "@app/risk/services/RiskDataService";
import { CustomFieldHelper } from "@app/shared/helpers/customField-helper";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { Pages } from "@constants/pages/Pages";
import { FilterType } from "@enums/FilterType";
import { FormatType } from "@enums/FormatType";
import { ModuleType } from "@enums/ModuleType";
import { RendererType } from "@enums/RendererType";
import { StandingDataType } from "@enums/StandingDataType";
import { TabEventListenerType } from "@enums/TabEventListenerType.enum";
import { FilterComponent } from "@enums/workspace/FilterComponent";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import { nameof } from "@methods/CommonMethods";
import { isGuid } from "@methods/uniqueMethods";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { WorkspaceButtonConfig } from "@models/workspace/WorkspaceButtonConfig";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { TabService } from "@services/tabs/TabService";
import { ActiveToast, ToastrService } from "ngx-toastr";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { ProcessedDocumentsService } from "@app/document-intelligence/services/processed-documents-service";
import { DocumentModel } from "@models/documents/documentmodel";
import { WorkflowStepItemModel } from "@app/shared/cerrix-workflow-bar/models/workflow-step-item";
import { WorkflowStepStatus } from "@app/shared/cerrix-workflow-bar/models/workflow-step-status.enum";
import { DocumentManagerService } from "@app/controls/testing/advanced/detail-new/common/services/document-manager.service";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "app-risk-overview",
    templateUrl: "./risk-overview.component.html",
    styleUrls: ["./risk-overview.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("risk")],
})
export class RiskOverviewComponent implements OnInit {
    private _riskDS = inject(RiskDataService);
    private standingDataService = inject(StandingdataDataService);
    private _tabs = inject(TabService);
    private _transloco = inject(TranslocoService);
    pages = inject(Pages);
    private _permissions = inject(PermissionsService);
    private toastr = inject(ToastrService);
    private appSettingsDs = inject(SettingsDataService);

    moduleType = ModuleType.ORM;
    tabID: string;
    tab: TabModel;
    data: Promise<any[]>;
    showCreai = false;
    headerLookup: { [propName: string]: string } = {};
    creaiDialogOption: "manual" | "ai" = "manual";
    creaiDocument: DocumentModel | null = null;
    creaiAllowedExtensions = ".pdf";
    creaiGeneratingRisks: boolean = false;
    private _dialogService = inject(CerrixDialogService);
    private _processedDocumentsService = inject(ProcessedDocumentsService);
    private _documentsService = inject(DocumentManagerService);
    protected documentConfig: {
        maxUploadSizeInMb: number;
        allowedFileExtensions: string;
    };

    public workflowSteps: WorkflowStepItemModel[] = [
        {
            id: "generateWithAi",
            name: "Generate with AI",
            status: WorkflowStepStatus.Active,
            infoTitle: "",
        },
        {
            id: "createSelection",
            name: "Create Selection",
            status: WorkflowStepStatus.Disabled,
            infoTitle: "",
        },
        {
            id: "refineAndCreate",
            name: "Refine & Create",
            status: WorkflowStepStatus.Disabled,
            infoTitle: "",
        },
    ];

    private getHeaderLookup(): { [propName: string]: string } {
        const t = (key: string) => this._transloco.translate(`risk.overview.${key}`);

        let headerLookup: any = {};
        headerLookup[nameof<RiskWorkspaceModel>("identifier")] = t("identifierHeader");
        headerLookup[nameof<RiskWorkspaceModel>("riskName")] = t("riskNameHeader");
        headerLookup[nameof<RiskWorkspaceModel>("grossScore")] = t("grossScoreHeader");
        headerLookup[nameof<RiskWorkspaceModel>("netScore")] = t("netScoreHeader");
        headerLookup[nameof<RiskWorkspaceModel>("riskAppetite")] = t("riskAppetiteHeader");
        headerLookup[nameof<RiskWorkspaceModel>("overallRiskAssessment")] =
            t("overallRiskAssessmentHeader");
        headerLookup[nameof<RiskWorkspaceModel>("assessmentDate")] = t("assessmentDateHeader");
        headerLookup[nameof<RiskWorkspaceModel>("organization")] = t("organizationHeader");
        headerLookup[nameof<RiskWorkspaceModel>("riskCatalogue")] = t("riskCatalogueHeader");
        headerLookup[nameof<RiskWorkspaceModel>("subtypes")] = t("subtypesHeader");
        headerLookup[nameof<RiskWorkspaceModel>("businessDimensions")] = t("businessDimensionsHeader");
        headerLookup[nameof<RiskWorkspaceModel>("frameworkDimensions")] = t("frameworkDimensionsHeader");
        headerLookup[nameof<RiskWorkspaceModel>("riskArea")] = t("riskAreaHeader");
        headerLookup[nameof<RiskWorkspaceModel>("rootCauseCategories")] = t("rootCauseCategoriesHeader");
        headerLookup[nameof<RiskWorkspaceModel>("cause")] = t("causeHeader");
        headerLookup[nameof<RiskWorkspaceModel>("effect")] = t("effectHeader");
        headerLookup[nameof<RiskWorkspaceModel>("grossImpact")] = t("grossImpactHeader");
        headerLookup[nameof<RiskWorkspaceModel>("grossLikelihood")] = t("grossLikelihoodHeader");
        headerLookup[nameof<RiskWorkspaceModel>("grossFinancialImpactPlaceholder")] = "-";
        headerLookup[nameof<RiskWorkspaceModel>("netImpact")] = t("netImpactHeader");
        headerLookup[nameof<RiskWorkspaceModel>("netLikelihood")] = t("netLikelihoodHeader");
        headerLookup[nameof<RiskWorkspaceModel>("netFinancialImpactPlaceholder")] = "-";
        headerLookup[nameof<RiskWorkspaceModel>("quantitativeScoringPlaceholder")] = "-";
        headerLookup[nameof<RiskWorkspaceModel>("confidential")] = t("confidentialHeader");
        headerLookup[nameof<RiskWorkspaceModel>("applicable")] = t("applicableHeader");
        headerLookup[nameof<RiskWorkspaceModel>("reviewed")] = t("reviewedHeader");
        headerLookup[nameof<RiskWorkspaceModel>("reviewedDate")] = t("reviewedDateHeader");
        headerLookup[nameof<RiskWorkspaceModel>("reviewedBy")] = t("reviewedByHeader");
        headerLookup[nameof<RiskWorkspaceModel>("approved")] = t("approvedHeader");
        headerLookup[nameof<RiskWorkspaceModel>("approvedDate")] = t("approvedDateHeader");
        headerLookup[nameof<RiskWorkspaceModel>("approvedBy")] = t("approvedByHeader");
        headerLookup[nameof<RiskWorkspaceModel>("keyRisk")] = t("keyRiskHeader");
        headerLookup[nameof<RiskWorkspaceModel>("owner")] = t("ownerHeader");
        headerLookup[nameof<RiskWorkspaceModel>("riskInformed")] = t("riskInformedHeader");
        headerLookup[nameof<RiskWorkspaceModel>("description")] = t("descriptionHeader");
        headerLookup[nameof<RiskWorkspaceModel>("treatment")] = t("treatmentHeader");
        headerLookup[nameof<RiskWorkspaceModel>("commentOnOverallRiskAssessment")] =
            t("commentOnOverallRiskAssessmentHeader");
        headerLookup[nameof<RiskWorkspaceModel>("comments")] = t("commentsHeader");
        headerLookup[nameof<RiskWorkspaceModel>("dateModified")] = t("dateModifiedHeader");
        headerLookup[nameof<RiskWorkspaceModel>("dateCreated")] = t("dateCreatedHeader");
        headerLookup[nameof<RiskWorkspaceModel>("connectedControls")] = t("connectedControlsHeader");
        headerLookup[nameof<RiskWorkspaceModel>("connectedMois")] = t("connectedMoisHeader");
        headerLookup[nameof<RiskWorkspaceModel>("connectedEvents")] = t("connectedEventsHeader");
        headerLookup[nameof<RiskWorkspaceModel>("numberOfDocuments")] = t("numberOfDocumentsHeader");
        headerLookup[nameof<RiskWorkspaceModel>("organizationPath")] = t("organizationPathHeader");
        headerLookup[nameof<RiskWorkspaceModel>("targetRiskPlaceholder")] = "-";
        headerLookup[nameof<RiskWorkspaceModel>("leadingImpactScale")] = t("leadingImpactScaleHeader");
        headerLookup[nameof<RiskWorkspaceModel>("financialImpactScoresPlaceholder")] = "-";
        headerLookup[nameof<RiskWorkspaceModel>("impactScalesPlaceholder")] = "-";
        headerLookup[nameof<RiskWorkspaceModel>("customFieldsPlaceholder")] = "-";

        return headerLookup;
    }

    filterConfigs: FilterConfig[] = [
        {
            label: "Organizations",
            column: nameof<RiskWorkspaceModel>("organizationID"),
            type: FilterType.Organization,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Business Dimensions",
            column: nameof<RiskWorkspaceModel>("businessDimensionIDs"),
            type: FilterType.BusinessDimensions,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Framework Dimensions",
            column: nameof<RiskWorkspaceModel>("frameworkDimensionIDs"),
            type: FilterType.FrameworkDimensions,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Risk Catalogues",
            column: nameof<RiskWorkspaceModel>("riskCatalogueID"),
            type: FilterType.RiskCatalogue,
            hideActionColumn: true,
            param: "",
            component: FilterComponent.ComboBox,
        },
        {
            label: "Period",
            column: "Period",
            type: FilterType.Period,
            param: "",
            module: ModuleType.ORM,
            remoteFilter: true,
        },
    ];

    rendererConfig: RendererConfig[] = [
        {
            textColumn: nameof<RiskWorkspaceModel>("grossImpact"),
            actionColumn: nameof<RiskWorkspaceModel>("grossImpactColor"),
            hideActionColumn: true,
            type: RendererType.Score,
            columnWidth: 200,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("grossLikelihood"),
            actionColumn: nameof<RiskWorkspaceModel>("grossLikelihoodColor"),
            hideActionColumn: true,
            type: RendererType.Score,
            columnWidth: 200,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("grossScore"),
            actionColumn: nameof<RiskWorkspaceModel>("grossScoreColor"),
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("netImpact"),
            actionColumn: nameof<RiskWorkspaceModel>("netImpactColor"),
            hideActionColumn: true,
            type: RendererType.Score,
            columnWidth: 200,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("netLikelihood"),
            actionColumn: nameof<RiskWorkspaceModel>("netLikelihoodColor"),
            hideActionColumn: true,
            type: RendererType.Score,
            columnWidth: 200,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("netScore"),
            actionColumn: nameof<RiskWorkspaceModel>("netScoreColor"),
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("overallRiskAssessment"),
            actionColumn: nameof<RiskWorkspaceModel>("overallRiskAssessmentColor"),
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("riskAppetite"),
            actionColumn: nameof<RiskWorkspaceModel>("riskAppetiteColor"),
            hideActionColumn: true,
            type: RendererType.Score,
        },

        {
            textColumn: nameof<RiskWorkspaceModel>("reviewedDate"),
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("approvedDate"),
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("dateModified"),
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("dateCreated"),
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: nameof<RiskWorkspaceModel>("assessmentDate"),
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
    ];

    buttonConfig: WorkspaceButtonConfig[] = [];

    numberProps: string[] = [
        nameof<RiskWorkspaceModel>("grossScore"),
        nameof<RiskWorkspaceModel>("netScore"),
        nameof<RiskWorkspaceModel>("numberOfDocuments"),
    ];

    dateProps: string[] = [
        nameof<RiskWorkspaceModel>("approvedDate"),
        nameof<RiskWorkspaceModel>("dateCreated"),
        nameof<RiskWorkspaceModel>("dateModified"),
        nameof<RiskWorkspaceModel>("reviewedDate"),
        nameof<RiskWorkspaceModel>("assessmentDate"),
    ];

    permissions: WorkspaceModulePermissionModel;

    currentlyLoadedPeriod = null;
    periodFilterToast: ActiveToast<any> = null;

    initialLoadCompleted = false;

    async ngOnInit(): Promise<void> {
        this.permissions = this._permissions.permissions.Workspace.risk;
        await firstValueFrom(this._transloco.load(`risk/${this._transloco.getActiveLang()}`));
        this.headerLookup = this.getHeaderLookup();
        this.data = this.getFormattedData();
        this.getCreaiSetting();
        this._documentsService.getDocumentConfig().subscribe((documentConfig) => {
            this.documentConfig = {
                ...documentConfig,
                allowedFileExtensions: documentConfig.allowedFileExtensions
                    .split(",")
                    .filter((ext) => this.creaiAllowedExtensions.includes(ext))
                    .join(","),
            };
        });

        this._tabs.listeners.addGlobalListener(
            TabEventListenerType.OnSave,
            this.pages.RiskDetail,
            () => {
                this.tab.refresh();
            },
            this.tab,
        );
    }

    async getFormattedData(periodID?: number): Promise<RiskWorkspaceModel[]> {
        const data = periodID
            ? await firstValueFrom(this._riskDS.getByPeriodId(this.currentlyLoadedPeriod))
            : await firstValueFrom(this._riskDS.getList());

        await this.configureCauseAndEffect();
        await this.configureQuantitativeScoring();

        const useTargetRisk = await firstValueFrom(
            this.appSettingsDs.getSetting(ApplicationSettings.EnableTargetRisk),
        );

        const useFinancialImpact = await firstValueFrom(
            this.appSettingsDs.getSetting(ApplicationSettings.EnableBudgetBasedRisk),
        );

        await this.configureTargetRisk(useTargetRisk.BoolValue, useFinancialImpact.BoolValue);
        await this.configureFinancialImpact(useFinancialImpact.BoolValue, useTargetRisk.BoolValue);

        // #6896 Make sure custom impact scales are the first dynamic fields that are added, because of order of fields.
        await this.configureImpactScales(useTargetRisk.BoolValue, data);
        await this.expandStandingDataCustomFields(data);
        await this.configureWorkspaceButtons();

        this.initialLoadCompleted = true;
        return this.mapHeaders(data);
    }

    async configureCauseAndEffect(): Promise<void> {
        const showCauseField = await firstValueFrom(
            this.appSettingsDs.getSetting(ApplicationSettings.ShowCauseField),
        );

        if (!showCauseField.BoolValue) {
            this.replaceHeaderLookupPlaceholder(nameof<RiskWorkspaceModel>("cause"), null);
        }

        const ShowEffectField = await firstValueFrom(
            this.appSettingsDs.getSetting(ApplicationSettings.ShowEffectField),
        );

        if (!ShowEffectField.BoolValue) {
            this.replaceHeaderLookupPlaceholder(nameof<RiskWorkspaceModel>("effect"), null);
        }
    }

    async configureQuantitativeScoring(): Promise<void> {
        const useQuantitativeScoring = await firstValueFrom(
            this.appSettingsDs.getSetting(ApplicationSettings.ShowQuantitativeScoringDetails),
        );

        const placeholderProp = nameof<RiskWorkspaceModel>("quantitativeScoringPlaceholder");
        if (!useQuantitativeScoring.BoolValue) {
            this.replaceHeaderLookupPlaceholder(placeholderProp, null);

            return;
        }

        const t = (key: string) => this._transloco.translate(`risk.overview.${key}`);
        const extraHeaders = {
            [nameof<RiskWorkspaceModel>("grossSingleLossExpectancy")]: t(
                "grossSingleLossExpectancyHeader",
            ),
            [nameof<RiskWorkspaceModel>("grossQuantitativeLikelihood")]: t(
                "grossQuantitativeLikelihoodHeader",
            ),
            [nameof<RiskWorkspaceModel>("grossAnnualizedLossExpectancy")]: t(
                "grossAnnualizedLossExpectancyHeader",
            ),
            [nameof<RiskWorkspaceModel>("netSingleLossExpectancy")]: t(
                "netSingleLossExpectancyHeader",
            ),
            [nameof<RiskWorkspaceModel>("netQuantitativeLikelihood")]: t(
                "netQuantitativeLikelihoodHeader",
            ),
            [nameof<RiskWorkspaceModel>("netAnnualizedLossExpectancy")]: t(
                "netAnnualizedLossExpectancyHeader",
            ),
        };

        this.replaceHeaderLookupPlaceholder(placeholderProp, extraHeaders);

        this.rendererConfig.push(
            {
                textColumn: nameof<RiskWorkspaceModel>("grossSingleLossExpectancy"),
                type: RendererType.Default,
                formatType: FormatType.DecimalFormat,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("grossAnnualizedLossExpectancy"),
                type: RendererType.Default,
                formatType: FormatType.DecimalFormat,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("netSingleLossExpectancy"),
                type: RendererType.Default,
                formatType: FormatType.DecimalFormat,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("netAnnualizedLossExpectancy"),
                type: RendererType.Default,
                formatType: FormatType.DecimalFormat,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("grossQuantitativeLikelihood"),
                type: RendererType.Default,
                formatType: FormatType.PercentFormat,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("netQuantitativeLikelihood"),
                type: RendererType.Default,
                formatType: FormatType.PercentFormat,
            },
        );

        this.numberProps.addRange([
            nameof<RiskWorkspaceModel>("grossSingleLossExpectancy"),
            nameof<RiskWorkspaceModel>("grossQuantitativeLikelihood"),
            nameof<RiskWorkspaceModel>("grossAnnualizedLossExpectancy"),
            nameof<RiskWorkspaceModel>("netSingleLossExpectancy"),
            nameof<RiskWorkspaceModel>("netQuantitativeLikelihood"),
            nameof<RiskWorkspaceModel>("netAnnualizedLossExpectancy"),
        ]);
    }

    async configureTargetRisk(useTargetRisk: boolean, useBudgetScoring): Promise<void> {
        const placeholderProp = nameof<RiskWorkspaceModel>("targetRiskPlaceholder");
        if (!useTargetRisk) {
            this.replaceHeaderLookupPlaceholder(placeholderProp, null);

            return;
        }

        const t = (key: string) => this._transloco.translate(`risk.overview.${key}`);
        let extraHeaders: { [x: string]: string };
        if (useBudgetScoring) {
            extraHeaders = {
                [nameof<RiskWorkspaceModel>("targetImpact")]: t("targetImpactHeader"),
                [nameof<RiskWorkspaceModel>("targetLikelihood")]: t("targetLikelihoodHeader"),
                [nameof<RiskWorkspaceModel>("financialImpactTarget")]: t(
                    "financialImpactTargetHeader",
                ),
                [nameof<RiskWorkspaceModel>("targetScore")]: t("targetScoreHeader"),
            };
        } else {
            extraHeaders = {
                [nameof<RiskWorkspaceModel>("targetImpact")]: t("targetImpactHeader"),
                [nameof<RiskWorkspaceModel>("targetLikelihood")]: t("targetLikelihoodHeader"),
                [nameof<RiskWorkspaceModel>("targetScore")]: t("targetScoreHeader"),
            };
        }

        this.replaceHeaderLookupPlaceholder(placeholderProp, extraHeaders);

        this.rendererConfig.push(
            {
                textColumn: nameof<RiskWorkspaceModel>("targetImpact"),
                actionColumn: nameof<RiskWorkspaceModel>("targetImpactColor"),
                hideActionColumn: true,
                type: RendererType.Score,
                columnWidth: 200,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("targetLikelihood"),
                actionColumn: nameof<RiskWorkspaceModel>("targetLikelihoodColor"),
                hideActionColumn: true,
                type: RendererType.Score,
                columnWidth: 200,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("targetScore"),
                actionColumn: nameof<RiskWorkspaceModel>("targetScoreColor"),
                hideActionColumn: true,
                type: RendererType.Score,
            },
        );

        this.numberProps.push(nameof<RiskWorkspaceModel>("targetScore"));

        if (useBudgetScoring) {
            this.numberProps.push(nameof<RiskWorkspaceModel>("financialImpactTarget"));
        }
    }

    async configureFinancialImpact(
        useBudgetScoring: boolean,
        useTargetRisk: boolean,
    ): Promise<void> {
        const grossImpactPlaceholder = nameof<RiskWorkspaceModel>(
            "grossFinancialImpactPlaceholder",
        );
        const netImpactPlaceholder = nameof<RiskWorkspaceModel>("netFinancialImpactPlaceholder");
        const scoresPlaceholder = nameof<RiskWorkspaceModel>("financialImpactScoresPlaceholder");

        if (!useBudgetScoring) {
            this.replaceHeaderLookupPlaceholder(grossImpactPlaceholder, null);
            this.replaceHeaderLookupPlaceholder(netImpactPlaceholder, null);
            this.replaceHeaderLookupPlaceholder(scoresPlaceholder, null);

            return;
        }

        const t = (key: string) => this._transloco.translate(`risk.overview.${key}`);

        const grossImpactReplacement = {
            [nameof<RiskWorkspaceModel>("financialImpactGross")]: t("financialImpactGrossHeader"),
        };

        this.replaceHeaderLookupPlaceholder(grossImpactPlaceholder, grossImpactReplacement);

        const netImpactReplacement = {
            [nameof<RiskWorkspaceModel>("financialImpactNet")]: t("financialImpactNetHeader"),
        };

        this.replaceHeaderLookupPlaceholder(netImpactPlaceholder, netImpactReplacement);

        let scoresReplacement: { [x: string]: string };
        if (useTargetRisk) {
            scoresReplacement = {
                [nameof<RiskWorkspaceModel>("financialImpactScoreGross")]: t(
                    "financialImpactScoreGrossHeader",
                ),
                [nameof<RiskWorkspaceModel>("financialImpactScoreNet")]: t(
                    "financialImpactScoreNetHeader",
                ),
                [nameof<RiskWorkspaceModel>("financialImpactScoreTarget")]: t(
                    "financialImpactScoreTargetHeader",
                ),
            };
        } else {
            scoresReplacement = {
                [nameof<RiskWorkspaceModel>("financialImpactScoreGross")]: t(
                    "financialImpactScoreGrossHeader",
                ),
                [nameof<RiskWorkspaceModel>("financialImpactScoreNet")]: t(
                    "financialImpactScoreNetHeader",
                ),
            };
        }

        this.replaceHeaderLookupPlaceholder(scoresPlaceholder, scoresReplacement);

        this.rendererConfig.push(
            {
                textColumn: nameof<RiskWorkspaceModel>("financialImpactScoreGross"),
                actionColumn: nameof<RiskWorkspaceModel>("financialImpactScoreGrossColor"),
                hideActionColumn: true,
                type: RendererType.Score,
                columnWidth: 200,
            },
            {
                textColumn: nameof<RiskWorkspaceModel>("financialImpactScoreNet"),
                actionColumn: nameof<RiskWorkspaceModel>("financialImpactScoreNetColor"),
                hideActionColumn: true,
                type: RendererType.Score,
                columnWidth: 200,
            },
        );

        if (useTargetRisk) {
            this.rendererConfig.push({
                textColumn: nameof<RiskWorkspaceModel>("financialImpactScoreTarget"),
                actionColumn: nameof<RiskWorkspaceModel>("financialImpactScoreTargetColor"),
                hideActionColumn: true,
                type: RendererType.Score,
                columnWidth: 200,
            });
        }

        this.numberProps.push(nameof<RiskWorkspaceModel>("financialImpactGross"));
        this.numberProps.push(nameof<RiskWorkspaceModel>("financialImpactNet"));
    }

    async getCreaiSetting() {
        this.showCreai = await firstValueFrom(this._processedDocumentsService.canAccess());
    }

    getData(keyWords: SearchKeyword[]): void {
        // For now there is always 1 serverfilter, which is the periodId
        this.currentlyLoadedPeriod = null;
        if (keyWords && keyWords.length > 0 && +keyWords[0].searchValue > 0) {
            this.currentlyLoadedPeriod = +keyWords[0].searchValue;
        }

        this.data = this.getFormattedData(this.currentlyLoadedPeriod);
    }

    async openDetail(risk: RiskWorkspaceModel): Promise<void> {
        if (this.currentlyLoadedPeriod > 0) {
            const response = await firstValueFrom(this._riskDS.getBaseGuidFromPeriodGuid(risk.guid));
            if (isGuid(response)) {
                if (response !== risk.guid) {
                    const msg = `Opening historical items is currently not available. Click here to open '${risk["Risk name"]}' in the current state.`;
                    this.createPeriodToast("Risk not available", msg, () => {
                        this.openTab(response, `(R) Risk`);
                    });

                    return;
                }
            } else {
                const msg = `Opening historical items is currently not available.`;
                this.createPeriodToast("Risk not available", msg, null);

                return;
            }
        }

        this.openTab(risk.guid, `(R) Risk`);
    }

    createPeriodToast(title: string, msg: string, onTab?: () => any): void {
        if (this.periodFilterToast) {
            this.periodFilterToast.toastRef.close();
        }

        this.periodFilterToast = this.toastr.info(msg, title);
        if (onTab) {
            this.periodFilterToast.onTap.subscribe(() => {
                onTab();
            });
        }
    }

    async add(): Promise<void> {
        this.showCreai = await firstValueFrom(this._processedDocumentsService.canAccess());
        if (this.showCreai) {
            this.creaiDocument = null;
            this.creaiDialogOption = "manual";
            this._dialogService.openDialog("addNewRisk");
        } else {
            this.addRisk();
        }
    }

    addRisk(): void {
        this.openTab(null, `(R) New Risk`);
    }

    private openTab(guid: string, name: string): void {
        this._tabs.generateTab(this.pages.RiskDetail, guid, name);
    }

    private async getStandingData(
        standingDataType: StandingDataType,
    ): Promise<IdNameCombination[]> {
        // Retrieve standing data fields
        const sd: IdNameCombination[] = await firstValueFrom(
            this.standingDataService.getMaintenanceListByType(standingDataType),
        );

        return sd;
    }

    private async expandStandingDataCustomFields(data: RiskWorkspaceModel[]): Promise<void> {
        const expandableFields = (
            await firstValueFrom(this.standingDataService.getAllByType(StandingDataType.RiskCustomField))
        ).filter((x) => x.visible);

        CustomFieldHelper.expandCustomFields(
            data,
            nameof<RiskWorkspaceModel>("customFields"),
            expandableFields,
            this.headerLookup,
            this.rendererConfig,
        );
    }

    private async configureWorkspaceButtons() {
        if (this.showCreai) {
            this.buttonConfig.push({
                text: "Extracted with AI",
                customTooltip: "",
                icon: "generate-ai",
                isDisabled: false,
                rowRequired: false,
                getVisibleRange: false,
                clickEvent: () =>
                    this._tabs.generateTab(
                        this.pages.NewRiskAiOverview,
                        null,
                        "AI Document Extraction",
                    ),
            });
        }
    }

    private mapHeaders = (rows: RiskWorkspaceModel[]): RiskWorkspaceModel[] => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    private async configureImpactScales(
        useTargetRisk: boolean,
        rows: RiskWorkspaceModel[],
    ): Promise<void> {
        const impactScales = await this.getStandingData(StandingDataType.RiskImpactScales);

        if (!this.initialLoadCompleted) {
            for (const impactScale of impactScales) {
                this.rendererConfig.push({
                    textColumn: "grossImpactScales_" + impactScale.ID,
                    actionColumn: "grossImpactScaleColors_" + impactScale.ID,
                    hideActionColumn: true,
                    type: RendererType.Score,
                    columnWidth: 200,
                });

                this.rendererConfig.push({
                    textColumn: "netImpactScales_" + impactScale.ID,
                    actionColumn: "netImpactScaleColors_" + impactScale.ID,
                    hideActionColumn: true,
                    type: RendererType.Score,
                    columnWidth: 200,
                });

                if (useTargetRisk) {
                    this.rendererConfig.push({
                        textColumn: "targetImpactScales_" + impactScale.ID,
                        actionColumn: "targetImpactScaleColors_" + impactScale.ID,
                        hideActionColumn: true,
                        type: RendererType.Score,
                        columnWidth: 200,
                    });
                }
            }
        }

        CustomFieldHelper.expandCustomFields(
            rows,
            nameof<RiskWorkspaceModel>("grossImpactScales"),
            impactScales,
            this.headerLookup,
            this.rendererConfig,
            (name) => name + " - Gross",
        );

        CustomFieldHelper.expandCustomFields(
            rows,
            nameof<RiskWorkspaceModel>("netImpactScales"),
            impactScales,
            this.headerLookup,
            this.rendererConfig,
            (name) => name + " - Net",
        );

        if (useTargetRisk) {
            CustomFieldHelper.expandCustomFields(
                rows,
                nameof<RiskWorkspaceModel>("targetImpactScales"),
                impactScales,
                this.headerLookup,
                this.rendererConfig,
                (name) => name + " - Target",
            );
        }

        CustomFieldHelper.expandCustomFields(
            rows,
            nameof<RiskWorkspaceModel>("grossImpactScaleColors"),
            impactScales,
            this.headerLookup,
            this.rendererConfig,
            (name) => name + " - Gross_Color",
        );

        CustomFieldHelper.expandCustomFields(
            rows,
            nameof<RiskWorkspaceModel>("netImpactScaleColors"),
            impactScales,
            this.headerLookup,
            this.rendererConfig,
            (name) => name + " - Net_Color",
        );

        if (useTargetRisk) {
            CustomFieldHelper.expandCustomFields(
                rows,
                nameof<RiskWorkspaceModel>("targetImpactScaleColors"),
                impactScales,
                this.headerLookup,
                this.rendererConfig,
                (name) => name + " - Target_Color",
            );
        }
    }

    private replaceHeaderLookupPlaceholder(
        placeholder: string,
        extraHeaders: { [propName: string]: string },
    ): void {
        if (extraHeaders && Object.keys(extraHeaders).length > 0) {
            // Reconstruct the object, adding the new entries in place of the placeholder
            let expandedObj: { [propName: string]: string } = {};

            // Iterate over the original object keys in order
            for (const key in this.headerLookup) {
                if (key === placeholder) {
                    // Add the new entries instead of "placeholder"
                    Object.assign(expandedObj, extraHeaders);
                } else {
                    // Copy the existing key-value pairs
                    expandedObj[key] = this.headerLookup[key];
                }
            }

            this.headerLookup = expandedObj;
        } else {
            if (placeholder in this.headerLookup) {
                delete this.headerLookup[placeholder]; // Remove the placeholder
            }
        }
    }
}
